import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Send } from "lucide-react";
import { SuccessModal } from "@/components/ui/SuccessModal";
import { createClientReferral, CreateClientReferralPayload } from "@/api/clientReferrals";
import { trackEvent } from "@/lib/firebase";

interface ClientReferralModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rewardAmount?: number;
}

interface ClientReferralFormData {
  companyName: string;
  pointOfContact: string;
  email: string;
  phoneNumber: string;
  website: string;
}

interface DuplicateAlert {
  show: boolean;
  title: string;
  description: string;
}

export function ClientReferralModal({ open, onOpenChange, rewardAmount = 5000 }: ClientReferralModalProps) {
  const { toast } = useToast();
  const { userData } = useAuth();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [duplicateAlert, setDuplicateAlert] = useState<DuplicateAlert>({ 
    show: false, 
    title: "", 
    description: "" 
  });
  const [formData, setFormData] = useState<ClientReferralFormData>({
    companyName: '',
    pointOfContact: '',
    email: '',
    phoneNumber: '',
    website: ''
  });

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
      // Reset form after closing
      setTimeout(() => {
        setFormData({
          companyName: '',
          pointOfContact: '',
          email: '',
          phoneNumber: '',
          website: ''
        });
        setShowSuccess(false);
      }, 300);
    }
  };

  // Custom URL validation function that accepts various formats
  const isValidURL = (url: string): boolean => {
    if (!url.trim()) return false;
    
    // Remove leading/trailing whitespace
    const trimmedUrl = url.trim();
    
    // Allow basic domain formats like:
    // - example.com
    // - www.example.com  
    // - https://example.com
    // - http://www.example.com
    // - subdomain.example.com
    const urlPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/;
    
    return urlPattern.test(trimmedUrl);
  };

  const handleInputChange = (field: keyof ClientReferralFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check authentication
    if (!userData?.id) {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Authentication Required",
        description: "You must be logged in to submit client referrals.",
        variant: "destructive"
      });
      return;
    }

    // Validate URL format
    if (formData.website && !isValidURL(formData.website)) {
      toast({
        title: "Invalid Website URL",
        description: "Please enter a valid website URL (e.g., example.com, www.example.com, or https://example.com)",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the client referral payload
      const clientReferralPayload: CreateClientReferralPayload = {
        companyName: formData.companyName.trim(),
        pointOfContact: formData.pointOfContact.trim(),
        email: formData.email.trim().toLowerCase(),
        website: formData.website.trim(),
        referredBy: userData.id, // Current user's Firebase Auth UID
        rewardAmount: rewardAmount // Use the passed reward amount from settings
      };

      // Only include phoneNumber if it has a value
      if (formData.phoneNumber && formData.phoneNumber.trim()) {
        clientReferralPayload.phoneNumber = formData.phoneNumber.trim();
      }

      await createClientReferral(clientReferralPayload);
      
      // Track client referral submission
      trackEvent('submit_client_referral', {
        company_name: formData.companyName,
        reward_amount: rewardAmount,
        user_id: userData.id,
      });
      
      // Invalidate client referrals query to trigger automatic refresh
      queryClient.invalidateQueries({ queryKey: ["clientReferrals", userData.id] });
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
      
      // Show success modal
      setShowSuccess(true);
      
    } catch (error: any) {      
      // Handle duplicate client referral error specifically
      if (error.code === 'already-exists' || 
          error.message?.includes('already exists') ||
          error.message?.includes('A client referral for this website already exists')) {
        
        // Remove focus from current element before showing alert
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        
        setDuplicateAlert({
          show: true,
          title: "Company Already Referred",
          description: "A client referral for this company already exists. You cannot refer the same company multiple times."
        });
        
        // Close the main modal to prevent ARIA conflicts
        onOpenChange(false);
        return;
      }
      
      // Handle other errors with toast
      let errorMessage = "Failed to submit client referral. Please try again.";
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.code) {
        // Handle other Firebase function error codes
        switch (error.code) {
          case 'functions/permission-denied':
          case 'permission-denied':
            errorMessage = "You don't have permission to create client referrals.";
            break;
          case 'functions/unauthenticated':
          case 'unauthenticated':
            errorMessage = "Please sign in to submit client referrals.";
            break;
          case 'functions/invalid-argument':
          case 'invalid-argument':
            errorMessage = "Invalid referral data. Please check all required fields.";
            break;
          case 'functions/internal':
          case 'internal':
            errorMessage = "A server error occurred. Please try again.";
            break;
          case 'functions/unavailable':
          case 'unavailable':
            errorMessage = "Service temporarily unavailable. Please try again later.";
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }
      
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = useCallback(() => {
    setFormData({
      companyName: '',
      pointOfContact: '',
      email: '',
      phoneNumber: '',
      website: ''
    });
    setShowSuccess(false);
    onOpenChange(false);
  }, [onOpenChange]);


  return (
    <>
      {/* Global Styles for Modal */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        /* Additional scrollbar hiding for all browsers */
        .dialog-content-wrapper *::-webkit-scrollbar {
          display: none;
        }
        .dialog-content-wrapper * {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* Specifically target all scrollable areas in the modal */
        .dialog-content-wrapper .overflow-y-auto,
        .dialog-content-wrapper .overflow-x-auto,
        .dialog-content-wrapper .overflow-auto {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .dialog-content-wrapper .overflow-y-auto::-webkit-scrollbar,
        .dialog-content-wrapper .overflow-x-auto::-webkit-scrollbar,
        .dialog-content-wrapper .overflow-auto::-webkit-scrollbar {
          display: none;
        }
        button[data-radix-dialog-close] {
          display: none !important;
        }
        .dialog-content-wrapper button[aria-label="Close"] {
          display: none !important;
        }
        .dialog-content-wrapper [data-radix-dialog-close] {
          display: none !important;
        }
        .dialog-content-wrapper .main-content {
          z-index: 1 !important;
          position: relative;
        }
        
        /* Prevent auto-scroll for form elements */
        .dialog-content-wrapper * {
          scroll-margin-top: 0 !important;
          scroll-margin-bottom: 0 !important;
          scroll-padding: 0 !important;
        }
        
        /* Override any scroll-into-view behavior */
        .dialog-content-wrapper input:focus,
        .dialog-content-wrapper button:focus {
          scroll-margin: 0 !important;
          scroll-behavior: auto !important;
        }
        
        /* Fix aria-hidden focus conflicts */
        [data-state="closed"][aria-hidden="true"] * {
          pointer-events: none !important;
        }
        
        [data-state="closed"][aria-hidden="true"] input,
        [data-state="closed"][aria-hidden="true"] button {
          visibility: hidden !important;
        }
      `}</style>

      {/* Success Message Overlay */}
      <SuccessModal
        isOpen={showSuccess}
        title="Client Referral Submitted! 🎉"
        message={`Your referral for ${formData.companyName} has been sent to our business development team for review.`}
        submessage="We'll reach out to them and keep you updated on the progress. You'll earn rewards when they become a client!"
        onClose={handleSuccessClose}
        highlightText={formData.companyName}
      />

      {/* Duplicate Referral Alert Dialog */}
      <AlertDialog open={duplicateAlert.show} onOpenChange={(open) => {
        if (!open) {
          setDuplicateAlert({ show: false, title: "", description: "" });
          // Reset form when alert is closed
          setFormData({
            companyName: '',
            pointOfContact: '',
            email: '',
            phoneNumber: '',
            website: ''
          });
        }
      }}>
        <AlertDialogContent className="bg-white border-red-200 shadow-2xl max-w-md z-[9999]">
          <AlertDialogHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <AlertDialogTitle className="text-xl font-bold text-slate-800">
              {duplicateAlert.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 mt-2 text-base">
              {duplicateAlert.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-center mt-6">
            <AlertDialogAction 
              onClick={() => {
                setDuplicateAlert({ show: false, title: "", description: "" });
                // Reset form when alert is closed
                setFormData({
                  companyName: '',
                  pointOfContact: '',
                  email: '',
                  phoneNumber: '',
                  website: ''
                });
              }}
              asChild
            >
              <Button variant="primary" size="md">
                Got it!
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent 
          className="dialog-content-wrapper w-[95vw] max-w-2xl h-[95vh] max-h-[95vh] p-0 border-0 shadow-2xl backdrop-blur-sm rounded-2xl overflow-hidden" 
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            handleClose();
          }}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          role="dialog"
          aria-modal="true"
        >
          {/* Required accessibility components - visually hidden */}
          <DialogTitle asChild>
            <VisuallyHidden>
              <h2 id="modal-title">Refer a Company to AiR Recruitment</h2>
            </VisuallyHidden>
          </DialogTitle>
          <DialogDescription asChild>
            <VisuallyHidden>
              <p id="modal-description">Submit a company referral to earn rewards when they become clients.</p>
            </VisuallyHidden>
          </DialogDescription>

          {/* Custom Close Button - iOS Safe Area Support */}
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute right-3 z-50 w-9 h-9 bg-white/90 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all duration-200 border border-gray-200/50 backdrop-blur-sm hover:bg-white disabled:opacity-50"
            style={{ top: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
            type="button"
            aria-label="Close modal"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Enhanced Background Effects */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50/20 via-transparent to-slate-50/30"></div>
            <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-orange-400/10 to-orange-600/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-48 h-48 bg-gradient-to-br from-orange-400/8 to-pink-400/6 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
          </div>
          
          {/* Main Content Container */}
          <div className="h-full relative main-content">
            <div className="absolute inset-0 overflow-y-auto scrollbar-hide">
              <div className="min-h-full">
                <div className="p-6 sm:p-8">

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Company Information */}
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <h3 className="text-xl font-bold text-slate-800">Company Information</h3>
                        <p className="text-slate-600">Tell us about the company you're referring</p>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="companyName">Company Name *</Label>
                          <Input
                            id="companyName"
                            value={formData.companyName}
                            onChange={(e) => handleInputChange('companyName', e.target.value)}
                            placeholder="Enter company name"
                            required
                            disabled={isSubmitting}
                            className="h-12"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="website">Company Website *</Label>
                          <Input
                            id="website"
                            type="text"
                            inputMode="url"
                            value={formData.website}
                            onChange={(e) => handleInputChange('website', e.target.value)}
                            placeholder="e.g., company.com or www.company.com"
                            required
                            disabled={isSubmitting}
                            autoComplete="url"
                            className="h-12"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <h3 className="text-xl font-bold text-slate-800">Contact Information</h3>
                        <p className="text-slate-600">Who should we reach out to at this company?</p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="pointOfContact">Point of Contact *</Label>
                          <Input
                            id="pointOfContact"
                            value={formData.pointOfContact}
                            onChange={(e) => handleInputChange('pointOfContact', e.target.value)}
                            placeholder="Enter contact person name"
                            required
                            disabled={isSubmitting}
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Contact Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            inputMode="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="Enter contact email"
                            required
                            disabled={isSubmitting}
                            autoComplete="email"
                            className="h-12"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phoneNumber">Contact Phone</Label>
                        <Input
                          id="phoneNumber"
                          type="tel"
                          inputMode="tel"
                          value={formData.phoneNumber}
                          onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                          placeholder="Enter contact phone (optional)"
                          disabled={isSubmitting}
                          autoComplete="tel"
                          className="h-12"
                        />
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-orange-900">What happens next?</h4>
                          <p className="text-sm text-orange-800 mt-1">
                            Our business development team will review your referral and reach out to the company within 2-3 business days. You'll earn a reward when they become a client and start using our services.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="border-t border-slate-200 bg-gradient-to-r from-white/80 to-slate-50/80 backdrop-blur-sm p-6 mt-8 rounded-lg">
                      <div className="flex flex-col sm:flex-row gap-3">
                      <Button 
                        type="button" 
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="flex-1 h-12"
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting || !formData.companyName || !formData.pointOfContact || !formData.email || !formData.website.trim() || !isValidURL(formData.website)}
                        className="flex-1 h-12"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Submitting...</span>
                          </div>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Submit Referral
                          </>
                        )}
                      </Button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
